import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { BgVideo, JOH_LOGO, JOH_LOGO_STYLE, P11, P11_LOGO, Particles } from "./p11shared";
import { useP11Lang } from "./p11lang";
import { PixelField, ScanSweep, SoundToggle, TechnicalGlyph, useP11Sound } from "./p11fx";

// Progressive home: ENTRY → EXPLANATION → PROTOCOL → registratie.
// Eén boodschap en één primaire actie per scherm. Reveal, don't overwhelm.
const SCREENS = 3;

export default function P11Home() {
  const nav = useNavigate();
  const { lang, toggle, tt } = useP11Lang();
  const { play } = useP11Sound();
  const [screen, setScreen] = useState(() => (localStorage.getItem("project11_intro_seen") ? "home" : 0));
  const [transitioning, setTransitioning] = useState(false);
  const [status, setStatus] = useState(null);
  // ingelogde deelnemer? optimistisch op token, daarna server-validatie
  const [loggedIn, setLoggedIn] = useState(() => !!localStorage.getItem("p11_token"));

  useEffect(() => {
    api.get("/project11/public/status").then(({ data }) => setStatus(data)).catch(() => setStatus(null));
    const token = localStorage.getItem("p11_token");
    if (token) {
      api.get("/project11/me/dashboard").then(() => setLoggedIn(true)).catch((err) => {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          localStorage.removeItem("p11_token");
          setLoggedIn(false);
        }
      });
    }
  }, []);

  const go = (target) => {
    play("click");
    // eerste bezoek: INITIALISEER voltooid → intro niet meer automatisch afspelen
    if (screen === 0 && target === 1) localStorage.setItem("project11_intro_seen", "1");
    if (target >= SCREENS) { nav("/project11/register"); return; }
    setTransitioning(true);
    setTimeout(() => { setScreen(target); setTransitioning(false); window.scrollTo(0, 0); }, 420);
  };

  const regLabel = !status ? "" : status.registration === "OPEN" ? tt("reg_open") : status.registration === "WAITLIST" ? tt("reg_waitlist") : tt("reg_closed");

  const protocol = [
    [tt("proto_period"), tt("proto_period_v")],
    [tt("proto_freq"), tt("proto_freq_v")],
    [tt("proto_total"), tt("proto_total_v")],
    [tt("proto_cost"), tt("proto_cost_v")],
    [tt("proto_cap"), tt("proto_cap_v")],
  ];

  return (
    <div className="p11-root relative overflow-hidden" data-testid="p11-home">
      {/* fullscreen achtergrondvideo + lees-overlay (gedeeld met registratie) */}
      <BgVideo />
      <Particles />
      <ScanSweep trigger={transitioning} />
      {/* zeldzame technische achtergrond-markeringen */}
      <div className="absolute top-24 right-8 opacity-30 pointer-events-none hidden md:block" aria-hidden="true"><TechnicalGlyph type="crosshair" size={22} /></div>
      <div className="absolute bottom-32 left-8 opacity-20 pointer-events-none hidden md:block" aria-hidden="true"><TechnicalGlyph type="signal" size={26} /></div>
      <div className={`relative z-10 min-h-screen flex flex-col ${transitioning ? "p11-fade-out" : "p11-fade-in"}`}>
        {/* Header */}
        <header className="flex items-center justify-between px-6 md:px-12 pt-6">
          <span className="p11-label" style={{ color: P11.text }}>JOH. BOXING</span>
          <div className="flex items-center gap-5">
            <SoundToggle />
            <button onClick={toggle} className="p11-label hover:text-white transition-colors" data-testid="p11-lang-toggle">{lang === "nl" ? "EN" : "NL"}</button>
            <span className="p11-label-yellow" data-testid="p11-system-label">{screen === 0 ? "SYSTEM / 11*" : tt("system_active")}</span>
          </div>
        </header>

        <main className="flex-1 flex flex-col justify-center px-6 md:px-12 max-w-3xl mx-auto w-full py-10">
          {screen === "home" && (
            /* ---------- TERUGKERENDE BEZOEKER: compact home ---------- */
            <section data-testid="p11-screen-home" className="relative">
              <div className="p11-label-yellow mb-10" data-testid="p11-home-label">PROJECT 11* / HOME</div>
              <img src={P11_LOGO} alt="PROJECT 11" className="h-12 md:h-16 object-contain mb-8" data-testid="p11-logo" />
              <div className="flex items-center gap-6 mt-2">
                {regLabel && <span className="p11-label" style={{ color: status?.registration === "OPEN" ? P11.yellow : P11.gray }} data-testid="p11-reg-status">{regLabel}</span>}
              </div>
              <div className="mt-14 flex flex-col items-start gap-5">
                {loggedIn ? (
                  <button onClick={() => { play("click"); nav("/project11/dashboard"); }} className="p11-btn" data-testid="p11-home-myproject">{tt("home_myproject")}</button>
                ) : (
                  <>
                    <button onClick={() => { play("click"); nav("/project11/register"); }} className="p11-btn" data-testid="p11-home-register">{tt("home_register")}</button>
                    <Link to="/project11/login" className="p11-label hover:text-white transition-colors border-b border-transparent hover:border-[#858585] pb-0.5" data-testid="p11-home-login">{tt("home_login")}</Link>
                  </>
                )}
                <button onClick={() => go(0)} className="p11-label hover:text-white transition-colors mt-4" style={{ color: P11.gray }} data-testid="p11-home-replay">{tt("home_replay")}</button>
              </div>
              <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-60 hidden md:block pointer-events-none" aria-hidden="true">
                <PixelField size={132} density={9} />
              </div>
            </section>
          )}

          {screen === 0 && (
            /* ---------- 01 ENTRY ---------- */
            <section data-testid="p11-screen-entry" className="relative">
              <div className="p11-label-yellow mb-10" data-testid="p11-intro-label">{tt("intro_label")}</div>
              <img src={P11_LOGO} alt="PROJECT 11" className="h-12 md:h-16 object-contain mb-8" data-testid="p11-logo" />
              <div className="p11-label" data-testid="p11-entry-subtitle">{tt("entry_subtitle")}</div>
              <button onClick={() => go(1)} className="p11-btn mt-16" data-testid="p11-initialize-btn">{tt("initialize")}</button>
              <div className="flex items-center gap-6 mt-6">
                {regLabel && <span className="p11-label" style={{ color: status?.registration === "OPEN" ? P11.yellow : P11.gray }} data-testid="p11-reg-status">{regLabel}</span>}
                <Link to="/project11/login" className="p11-label hover:text-white transition-colors border-b border-transparent hover:border-[#858585] pb-0.5" data-testid="p11-login-link">{tt("login")}</Link>
              </div>
              {/* klein evoluerend dot-matrix kunstwerk — sfeer, geen competitie met de titel */}
              <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-60 hidden md:block pointer-events-none" aria-hidden="true">
                <PixelField size={132} density={9} />
              </div>
            </section>
          )}

          {screen === 1 && (
            /* ---------- 02 PROJECT EXPLANATION ---------- */
            <section data-testid="p11-screen-explanation">
              <div className="flex items-center justify-between p11-label mb-14">
                <span className="inline-flex items-center gap-2"><TechnicalGlyph type="crosshair" size={13} active />PROJECT 11* / {lang === "nl" ? "BETEKENIS" : "PURPOSE"}</span>
                <span>{tt("file_001")}</span>
              </div>
              <hr className="p11-divider p11-line-anim" />
              <h1 className="p11-h1 text-4xl sm:text-5xl lg:text-6xl mt-14" data-testid="p11-exp-headline">{tt("exp_headline")}</h1>
              <p className="mt-8 text-base md:text-lg max-w-xl leading-relaxed" style={{ color: P11.gray2 }} data-testid="p11-exp-body">{tt("exp_body")}</p>
              <div className="mt-14 flex items-center gap-6">
                <button onClick={() => go(2)} className="p11-btn" data-testid="p11-continue-btn">{tt("continue_btn")}</button>
                <button onClick={() => go(0)} className="p11-label hover:text-white transition-colors" data-testid="p11-back-0">{tt("back")}</button>
              </div>
            </section>
          )}

          {screen === 2 && (
            /* ---------- 03 EXPERIMENT PROTOCOL ---------- */
            <section data-testid="p11-screen-protocol">
              <div className="flex items-center justify-between p11-label mb-14">
                <span className="inline-flex items-center gap-2"><TechnicalGlyph type="square" size={13} active />PROJECT 11* / PROTOCOL</span>
                <span>{tt("file_001")}</span>
              </div>
              <hr className="p11-divider p11-line-anim" />
              <div className="p11-label-yellow mt-14">{tt("protocol_title")}</div>
              <div className="mt-8 divide-y" style={{ borderColor: P11.border }} data-testid="p11-protocol">
                {protocol.map(([k, v], i) => (
                  <div key={k} className="py-5 flex items-baseline justify-between gap-6 p11-fade-in" style={{ animationDelay: `${i * 90}ms` }} data-testid={`p11-proto-${i}`}>
                    <span className="p11-label">{k}</span>
                    <span className="p11-mono text-base md:text-xl text-right" style={{ color: P11.text }}>{v}</span>
                  </div>
                ))}
              </div>
              <p className="p11-label mt-6" style={{ color: P11.gray }} data-testid="p11-proto-note">{tt("fixed_info")}</p>
              <div className="mt-12 flex items-center gap-6">
                <button onClick={() => go(3)} className="p11-btn" data-testid="p11-proceed-btn">{tt("proceed")}</button>
                <button onClick={() => go(1)} className="p11-label hover:text-white transition-colors" data-testid="p11-back-1">{tt("back")}</button>
              </div>
            </section>
          )}
        </main>

        {/* JOH. logo onderin gecentreerd — stille handtekening */}
        <footer className="relative z-10 flex justify-center pt-4" style={{ paddingBottom: "max(2rem, env(safe-area-inset-bottom))" }}>
          <img src={JOH_LOGO} alt="JOH. Boxing" className="object-contain" style={JOH_LOGO_STYLE} data-testid="p11-joh-logo" />
        </footer>
      </div>
    </div>
  );
}
